import apiClient from "./apiClient";
import { isOfflineMode } from "@/helpers/offlineMode";

export const FINANCE_COMPANIES = [
  { id: 8, name: "Advantage Finance" },
  { id: 50, name: "Alphera" },
  { id: 68, name: "AutoLend" },
  { id: 71, name: "Automoney" },
  { id: 29, name: "Billing Finance" },
  { id: 63, name: "BNP Paribas Finance" },
  { id: 76, name: "Camco Finance" },
  { id: 49, name: "Carfinance247" },
  { id: 77, name: "Carmoola" },
  { id: 70, name: "Close Brothers" },
  { id: 74, name: "Credit Agricole" },
  { id: 81, name: "Go Car Credit" },
  { id: 79, name: "JBR Capital" },
  { id: 62, name: "Mann Island Finance Ltd" },
  { id: 80, name: "Marshmallow" },
  { id: 9, name: "Moneybarn No 1 Ltd." },
  { id: 67, name: "Motonovo" },
  { id: 82, name: "Nestra Lending Limited" },
  { id: 75, name: "Northridge Finance" },
  { id: 58, name: "Oodle" },
  { id: 39, name: "Startline Finance" },
  { id: 72, name: "Tandem" },
  { id: 78, name: "Volkswagen Financial Services (UK) Limited" },
  { id: 73, name: "Zopa" },
] as const;

export const PROPOSAL_DECISIONS = [
  { id: 1, name: "Processing" },
  { id: 2, name: "Declined" },
  { id: 3, name: "Provisional Acceptance" },
  { id: 4, name: "Accepted" },
  { id: 5, name: "Conditionally Accepted" },
  { id: 6, name: "Failed Send Rules" },
  { id: 7, name: "More Info" },
  { id: 8, name: "Manual Processing" },
  { id: 9, name: "Expired" },
] as const;

export interface ProposalTier {
  id: string;
  name: string;
}

interface FinanceCompanyTier {
  financeCompanyId: number;
  tierId: number;
  tier: string;
  activeFlag: boolean;
}

export interface AddDecisionProposal {
  applicationId: number;
  financeCompanyId: number;
  financeCompanyTierId: string;
  loanProposalStatusId: number;
  lenderReference: string;
}

export interface LoanProposalCreatePayload {
  loanApplicationId: number;
  financeCompanyId: number;
  financeCompanyTierId: number;
  loanProposalStatusId: number;
  financeCompanyReference: string;
  createdOn: string;
  modifiedOn: string;
  modifiedBy: string;
}

export function mapAddDecisionProposalToLoanProposalRequest(
  proposal: AddDecisionProposal,
  createdOn = new Date()
): LoanProposalCreatePayload {
  const timestamp = createdOn.toISOString();
  return {
    loanApplicationId: proposal.applicationId,
    financeCompanyId: proposal.financeCompanyId,
    financeCompanyTierId: Number(proposal.financeCompanyTierId),
    loanProposalStatusId: proposal.loanProposalStatusId,
    financeCompanyReference: proposal.lenderReference,
    createdOn: timestamp,
    modifiedOn: timestamp,
    modifiedBy: "DealerDirectQueue",
  };
}

export interface DecisionProposal {
  proposalId: number;
  loanProposalStatus: string;
  lenderReference: string | null;
  modifiedOn: string | null;
  lenderName: string;
  financeCompanyId: number | null;
  isPreferredAcceptance?: boolean;
  firmDecisionReceived?: string | null;
  customerRuleResults?: { resultType: string; message: string }[] | null;
  aprRange?: string | null;
}

export interface LenderDecision extends DecisionProposal {
  status: string;
  fixedDeclinedReason: string | null;
}

export function mapDecisionHistory(proposals: DecisionProposal[]): LenderDecision[] {
  const ranked = proposals.map((proposal) => {
    const status = proposal.loanProposalStatus.replace(/([a-z])([A-Z])/g, "$1 $2").trim();
    const fixedDeclinedReason = status !== "Accepted" && status !== "Declined"
      ? proposal.customerRuleResults?.filter((rule) => rule.resultType === "FixedDecline").map((rule) => rule.message).join(" & ") || null
      : null;
    return { ...proposal, status: fixedDeclinedReason ? "Non Qualifying" : status, fixedDeclinedReason };
  });
  const rank = (decision: LenderDecision) => {
    if (["Accepted", "Provisional Acceptance", "Conditionally Accepted"].includes(decision.status) ||
      (decision.status === "Processing" && decision.firmDecisionReceived)) return 1;
    return decision.status === "Non Qualifying" ? 2 : 3;
  };
  return ranked.sort((first, second) =>
    Number(!!second.isPreferredAcceptance) - Number(!!first.isPreferredAcceptance) || rank(first) - rank(second)
  );
}

const getHistory = async (applicationId: number, signal?: AbortSignal): Promise<LenderDecision[]> => {
  if (isOfflineMode()) return [];
  const { data } = await apiClient.get<DecisionProposal[]>(
    `/application/${applicationId}/available-proposals/v3/expand-prime`, { signal }
  );
  return mapDecisionHistory(data);
};

const rescore = async (applicationId: number): Promise<void> => {
  if (isOfflineMode()) return;
  await apiClient.post(`/application/rescore/${applicationId}`);
};

const getTiers = async (financeCompanyId: number, signal?: AbortSignal): Promise<ProposalTier[]> => {
  const { data } = await apiClient.get<FinanceCompanyTier[]>("/quote-underwriting/queue/get-tiers", {
    params: { financeCompanyId },
    signal,
  });
  return data
    .filter((tier) => tier.activeFlag && tier.financeCompanyId === financeCompanyId)
    .map((tier) => ({ id: String(tier.tierId), name: tier.tier }));
};

const addProposal = async (payload: AddDecisionProposal): Promise<void> => {
  await apiClient.post(
    "/quote-underwriting/queue/loan-proposal",
    mapAddDecisionProposalToLoanProposalRequest(payload)
  );
};

export const lenderDecisionService = { getHistory, rescore, getTiers, addProposal };